import { IPeriodFilterData } from 'components/Filter/PeriodFilter';
import { DashboardSharedData } from 'models/dashboard';
import Widget, { IWidgetCustomAction, WidgetTypes } from 'models/widget';
import WidgetActiveAuditsForUser from './Widgets/WidgetActiveAuditsForUser';
import WidgetAuditCriteriaToReview from './Widgets/WidgetAuditCriteriaToReview';
import WidgetCustomMyNormCoverage from './Widgets/WidgetCustomMyNormCoverage';
import WidgetCustomNormCoverage from './Widgets/WidgetCustomNormCoverage';
import WidgetLibraryContent from './Widgets/WidgetLibraryContent';
import WidgetLibraryMyItems from './Widgets/WidgetLibraryMyItems';
import WidgetLibraryReadResponse from './Widgets/WidgetLibraryReadResponse';
import WidgetLibraryReadResponseAudiences from './Widgets/WidgetLibraryReadResponseAudiences';
import WidgetLibraryReadStats from './Widgets/WidgetLibraryReadStats';
import WidgetMicrosoftSecureScores from './Widgets/WidgetMicrosoftSecureScores';
import WidgetMyActions from './Widgets/WidgetMyActions';
import WidgetMyCurrentTasks from './Widgets/WidgetMyCurrentTasks';
import WidgetMyNormCoverage from './Widgets/WidgetMyNormCoverage';
import WidgetMyOverdueTasks from './Widgets/WidgetMyOverdueTasks';
import WidgetMyRecentItems from './Widgets/WidgetMyRecentItems';
import WidgetMyUpcomingTasks from './Widgets/WidgetMyUpcomingTasks';
import WidgetNormCoverage from './Widgets/WidgetNormCoverage';
import WidgetOpenTasksPerUser from './Widgets/WidgetOpenTasksPerUser';
import WidgetProjectNextStep from './Widgets/WidgetProjectNextStep';
import WidgetRecentlyClosedTasks from './Widgets/WidgetRecentlyClosedTasks';
import WidgetRiskOwner from './Widgets/WidgetRiskOwner';
import WidgetRiskStatus from './Widgets/WidgetRiskStatus';
import WidgetRiskStatusNotAccepted from './Widgets/WidgetRiskStatusNotAccepted';
import WidgetSingleKPI from './Widgets/WidgetSingleKPI';
import WidgetSingleKPILastPeriod from './Widgets/WidgetSingleKPILastPeriod';
import WidgetSingleRisk from './Widgets/WidgetSingleRisk';
import WidgetSingleTask from './Widgets/WidgetSingleTask';
import WidgetTaskAction from './Widgets/WidgetTaskAction';
import WidgetTaskCompletedDeadline from './Widgets/WidgetTaskCompletedDeadline';
import WidgetTaskStatus from './Widgets/WidgetTaskStatus';
import WidgetTaskTag from './Widgets/WidgetTaskTag';
import WidgetTenantControlChanges from './Widgets/WidgetTenantControlChanges';
import WidgetTenantControlStatus from './Widgets/WidgetTenantControlStatus';
import WidgetTenantListApprovalStatus from './Widgets/WidgetTenantListApprovalStatus';
import WidgetTenantOverview from './Widgets/WidgetTenantOverview';
import WidgetTenantRiskChanges from './Widgets/WidgetTenantRiskChanges';
import WidgetTenantRiskOwner from './Widgets/WidgetTenantRiskOwner';
import WidgetTenantRiskStatus from './Widgets/WidgetTenantRiskStatus';
import WidgetTenantTaskStatus from './Widgets/WidgetTenantTaskStatus';
import WidgetTenantUserStats from './Widgets/WidgetTenantUserStats';

export interface IWidgetRendererProps {
  widget: Widget;
  sharedData?: DashboardSharedData;
  period?: IPeriodFilterData;
  abortSignal?: AbortSignal;
  onRegisterCustomActions?: (widget: Widget, customActions: IWidgetCustomAction[]) => void;
  onRefreshSharedData?: () => Promise<void>;

  /** Set on a project dashboard; a widget that supports it scopes its query to this project. */
  projectId?: number;
}

const WidgetRenderer = (props: IWidgetRendererProps) => {
  switch (props.widget.widgetType) {
    case WidgetTypes.LibraryContent:
      return <WidgetLibraryContent {...props} />;
    case WidgetTypes.MyOverdueTasks:
      return <WidgetMyOverdueTasks {...props} />;
    case WidgetTypes.MyUpcomingTasks:
      return <WidgetMyUpcomingTasks {...props} />;
    case WidgetTypes.SingleRisk:
      return <WidgetSingleRisk {...props} />;
    case WidgetTypes.MyRecentItems:
      return <WidgetMyRecentItems {...props} />;
    case WidgetTypes.MyActions:
      return <WidgetMyActions {...props} />;
    case WidgetTypes.RecentlyClosedTasks:
      return <WidgetRecentlyClosedTasks {...props} />;
    case WidgetTypes.OpenTasksPerUser:
      return <WidgetOpenTasksPerUser {...props} />;
    case WidgetTypes.NormCoverage:
      return <WidgetNormCoverage {...props} />;
    case WidgetTypes.CustomNormCoverage:
      return <WidgetCustomNormCoverage {...props} />;
    case WidgetTypes.RiskStatus:
      return <WidgetRiskStatus {...props} />;
    case WidgetTypes.RiskStatusNotAccepted:
      return <WidgetRiskStatusNotAccepted {...props} />;
    case WidgetTypes.MyNormCoverage:
      return <WidgetMyNormCoverage {...props} />;
    case WidgetTypes.CustomMyNormCoverage:
      return <WidgetCustomMyNormCoverage {...props} />;
    case WidgetTypes.SingleTask:
      return <WidgetSingleTask {...props} />;
    case WidgetTypes.MicrosoftSecureScores:
      return <WidgetMicrosoftSecureScores {...props} />;
    case WidgetTypes.TaskStatus:
      return <WidgetTaskStatus {...props} />;
    case WidgetTypes.TaskCompletedDeadline:
      return <WidgetTaskCompletedDeadline {...props} />;
    case WidgetTypes.SingleKPI:
      return <WidgetSingleKPI {...props} />;
    case WidgetTypes.SingleKPILastPeriod:
      return <WidgetSingleKPILastPeriod {...props} />;
    case WidgetTypes.LibraryReadResponse:
      return <WidgetLibraryReadResponse {...props} />;
    case WidgetTypes.TaskTag:
      return <WidgetTaskTag {...props} />;
    case WidgetTypes.LibraryReadStats:
      return <WidgetLibraryReadStats {...props} />;
    case WidgetTypes.TaskAction:
      return <WidgetTaskAction {...props} />;
    case WidgetTypes.TenantRiskStatusPartner:
    case WidgetTypes.TenantRiskStatusOrgUnit:
      return <WidgetTenantRiskStatus {...props} />;
    case WidgetTypes.TenantRiskChangesPartner:
    case WidgetTypes.TenantRiskChangesOrgUnit:
      return <WidgetTenantRiskChanges {...props} />;
    case WidgetTypes.RiskOwner:
      return <WidgetRiskOwner {...props} />;
    case WidgetTypes.TenantRiskOwnerPartner:
    case WidgetTypes.TenantRiskOwnerOrgUnit:
      return <WidgetTenantRiskOwner {...props} />;
    case WidgetTypes.TenantControlStatusPartner:
    case WidgetTypes.TenantControlStatus:
      return <WidgetTenantControlStatus {...props} />;
    case WidgetTypes.TenantControlChangesPartner:
    case WidgetTypes.TenantControlChangesOrgUnit:
      return <WidgetTenantControlChanges {...props} />;
    case WidgetTypes.TenantListApprovalStatusPartner:
    case WidgetTypes.TenantListApprovalStatusOrgUnit:
      return <WidgetTenantListApprovalStatus {...props} />;
    case WidgetTypes.TenantTaskStatusPartner:
    case WidgetTypes.TenantTaskStatusOrgUnit:
      return <WidgetTenantTaskStatus {...props} />;
    case WidgetTypes.TenantUserStatsPartner:
    case WidgetTypes.TenantUserStatsOrgUnit:
      return <WidgetTenantUserStats {...props} />;
    case WidgetTypes.TenantOverview:
      return <WidgetTenantOverview {...props} />;
    case WidgetTypes.LibraryMyItems:
      return <WidgetLibraryMyItems {...props} />;
    case WidgetTypes.LibraryReadResponseAudiences:
      return <WidgetLibraryReadResponseAudiences {...props} />;
    case WidgetTypes.MyCurrentTasks:
      return <WidgetMyCurrentTasks {...props} />;
    case WidgetTypes.ActiveAuditsForUser:
      return <WidgetActiveAuditsForUser {...props} />;
    case WidgetTypes.AuditCriteriaToReview:
      return <WidgetAuditCriteriaToReview {...props} />;
    case WidgetTypes.ProjectNextStep:
      return <WidgetProjectNextStep {...props} />;
    default:
      return null;
  }
};

export default WidgetRenderer;
